/**
 * 设备信息管理器
 *
 * 统一管理所有设备相关信息的获取、生成和存储
 * 消除各 API 类中重复的设备信息获取逻辑
 */

import type { DeviceStore } from '../stores/DeviceStore'
import type { SourceStore } from '../stores/SourceStore'
import type { IStorage } from '../storage/Storage'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { getVersion, getOS } from '../config/SDKConfigManager'
import { getDeviceId as getLegacyDeviceId } from '../utils/device'

/**
 * 推广参数接口
 */
export interface PromotionParams {
  mp?: string
  bdVid?: string
  qhclickid?: string
  strategy?: string
  invite_id?: number
}

/**
 * 公共参数接口
 */
export interface CommonParams {
  curr_ver: string
  soft_id: string
  mac_code: string
  device_code: string
  os: number
  source: string
  device_id: string
  ekey: string
}

/**
 * 设备信息管理器接口
 */
export interface IDeviceInfoManager {
  getDeviceId(): string
  getDeviceCode(): string
  getEkey(): string
  getMacCode(): string
  getSource(): string
  getPromotionParams(): PromotionParams
  getCommonParams(): CommonParams
}

/**
 * 设备信息管理器实现
 */
export class DeviceInfoManager implements IDeviceInfoManager {
  private deviceStore: DeviceStore
  private sourceStore: SourceStore
  private storage: IStorage

  constructor(deviceStore: DeviceStore, sourceStore: SourceStore, storage: IStorage) {
    this.deviceStore = deviceStore
    this.sourceStore = sourceStore
    this.storage = storage
  }

  /**
   * 获取设备 ID（基于 UserAgent 判断设备类型）
   *
   * 返回值：
   * - 'server': 服务端环境
   * - 'applet': 微信小程序
   * - 'Window32': Windows 32位
   * - 'Window64': Windows 64位
   * - 'Mac': macOS
   * - 'Android_Phone': Android 手机
   * - 'IOS_Phone': iOS 手机
   * - 'ipad': iPad
   * - 时间戳 + 'pc': 其他 PC 设备
   */
  getDeviceId(): string {
    return getLegacyDeviceId()
  }

  /**
   * 获取设备码（16位 UUID）
   * 从 DeviceStore 读取，如果不存在会自动生成
   */
  getDeviceCode(): string {
    return this.deviceStore.deviceCode
  }

  /**
   * 获取 ekey（16位特定格式）
   * 从 DeviceStore 读取，如果不存在会自动生成
   */
  getEkey(): string {
    return this.deviceStore.ekey
  }

  /**
   * 获取 MAC 码（等于 device_code）
   */
  getMacCode(): string {
    return this.getDeviceCode()
  }

  /**
   * 获取来源渠道
   */
  getSource(): string {
    return this.sourceStore.source
  }

  /**
   * 获取所有推广参数
   */
  getPromotionParams(): PromotionParams {
    const params: PromotionParams = {}

    const mp = this.sourceStore.mp || undefined
    if (mp) {
      params.mp = mp
    }
    if (this.sourceStore.bdVid) {
      params.bdVid = this.sourceStore.bdVid
    }
    if (this.sourceStore.qhclickid) {
      params.qhclickid = this.sourceStore.qhclickid
    }
    if (this.sourceStore.strategy) {
      params.strategy = this.sourceStore.strategy
    }

    // 从存储读取 invite_id（如果存在）
    let inviteIdRaw: string | number | null = this.storage.getItem<number | string>(
      STORAGE_KEYS.INVITE_ID
    )
    if (
      (inviteIdRaw === null || inviteIdRaw === undefined) &&
      typeof localStorage !== 'undefined'
    ) {
      try {
        inviteIdRaw = localStorage.getItem('invite_id')
      } catch {
        // ignore
      }
    }
    if (inviteIdRaw !== null && inviteIdRaw !== undefined) {
      const inviteId = typeof inviteIdRaw === 'string' ? parseInt(inviteIdRaw, 10) : inviteIdRaw
      if (!Number.isNaN(inviteId)) {
        params.invite_id = inviteId
      }
    }

    return params
  }

  /**
   * 获取完整的公共参数对象
   * 包含所有 API 请求需要的公共参数
   */
  getCommonParams(): CommonParams {
    const softId = this.deviceStore.softId || ''

    return {
      curr_ver: getVersion(),
      soft_id: softId,
      mac_code: this.getMacCode(),
      device_code: this.getDeviceCode(),
      os: getOS(),
      source: this.getSource(),
      device_id: this.getDeviceId(),
      ekey: this.getEkey()
    }
  }
}
